import api from "./api";
import { isoDateTimeWithDashes } from "../utils/DateTime";
import Logger from "../utils/Logger";

/**
 * Extracts the filename from the Content-Disposition header.
 * @param contentDisposition - The Content-Disposition header value.
 * @returns The extracted filename or null if not found.
 */
const getFilenameFromContentDisposition = (
  contentDisposition: string,
): string | null => {
  if (!contentDisposition) {
    Logger.debug("No Content-Disposition header found", ["ReportApi"]);
    return null;
  }

  let filename: string | null = null;

  // First, try to extract filename* (UTF-8 encoded)
  const utf8FilenameRegex = /filename\*=UTF-8''([\w%\-\.]+)((?:; ?)|$)/i;
  const utf8Match = contentDisposition.match(utf8FilenameRegex);

  if (utf8Match && utf8Match[1]) {
    filename = decodeURIComponent(utf8Match[1]);
    Logger.debug("Extracted UTF-8 filename from Content-Disposition", [
      "ReportApi",
    ]);
  } else {
    // If filename* is not found, try to extract filename
    const filenameStart = contentDisposition.toLowerCase().indexOf("filename=");
    if (filenameStart >= 0) {
      const partialDisposition = contentDisposition.slice(filenameStart);
      const asciiFilenameRegex = /^filename=(["']?)(.*?)(?:; ?|$)/i;
      const asciiMatch = partialDisposition.match(asciiFilenameRegex);

      if (asciiMatch && asciiMatch[2]) {
        // Remove quotes if present
        filename = asciiMatch[2].replace(/^["']|["']$/g, "");
        Logger.debug("Extracted ASCII filename from Content-Disposition", [
          "ReportApi",
        ]);
      }
    }
  }

  if (!filename) {
    Logger.warn("Could not extract filename from Content-Disposition header", [
      "ReportApi",
    ]);
  }

  return filename;
};

/**
 * Fetches the CSV report from the `/report/export-csv` endpoint.
 * @returns A Blob containing the CSV data and the extracted filename.
 */
const fetchExportCSV = async (): Promise<{ data: Blob; filename: string }> => {
  try {
    Logger.info("Starting CSV export API request", ["ReportApi"]);

    const response = await api
      .get("/report/export-csv", {
        responseType: "blob", // Ensure the response is treated as a file
      })
      .then((res) => {
        Logger.info("CSV export API request completed successfully", [
          "ReportApi",
        ]);
        return res;
      })
      .catch((err) => {
        Logger.error("Error in CSV export API call", err, ["ReportApi"]);
        throw err;
      });

    const contentDisposition: string =
      response.headers["content-disposition"] || "";

    const filename =
      getFilenameFromContentDisposition(contentDisposition) ||
      `scrm-export-${isoDateTimeWithDashes}.csv`;

    Logger.info("CSV export completed with filename", ["ReportApi"], {
      filename,
      hasContentDisposition: !!contentDisposition,
    });

    return {
      data: response.data, // Return the raw Blob data
      filename,
    };
  } catch (error) {
    Logger.error("Error fetching export CSV", error, ["ReportApi"]);
    throw error;
  }
};

export const ReportApi = {
  fetchExportCSV,
};
